import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, ButtonGroup } from 'react-bootstrap'
import { issueDestinationAPI } from '../api/issueDestinationAPI'
import ResizableTable from '../components/ResizableTable'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function IssueDestinationsListScreen() {
    const { hasPermission } = usePermissions()
    const [destinations, setDestinations] = useState([])
    const [showDeactivated, setShowDeactivated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('issueDestinationColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            name: 'auto',
            full_name: 'auto',
            contact: 'auto',
            bill_to_address: 'auto',
            ship_to_address: 'auto',
            phone: 'auto',
            email: 'auto',
            subcategories_count: 100,
            actions: 100
        };
    });

    useEffect(() => {
        localStorage.setItem('issueDestinationColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchDestinations() {
            try {
                setLoading(true)
                const params = {
                    search: searchTerm || undefined,
                    is_active: showDeactivated ? undefined : true
                }
                const response = await issueDestinationAPI.getAll(params)
                setDestinations(response)
            } finally {
                setLoading(false)
            }
        }
        fetchDestinations()
    }, [searchTerm, showDeactivated])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            () => {}, // No page reset for simple list
            debounceTimeoutRef
        );
    };

    const handleToggleActive = async (e, destination) => {
        e.stopPropagation();
        
        if (destination.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this destination?')) {
                return;
            }
        }

        try {
            const updatedData = {
                ...destination,
                is_active: !destination.is_active
            };
            const updatedDestination = await issueDestinationAPI.update(destination._id, updatedData);
            
            setDestinations(destinations.map(d => 
                d._id === destination._id 
                    ? updatedDestination
                    : d
            ));
        } catch (error) {
            console.error('Error toggling destination status:', error);
            alert(`Failed to update destination: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    const handleDelete = async (e, destination) => {
        e.stopPropagation();
        
        if (!window.confirm('Are you sure you want to permanently delete this destination? This action cannot be undone.')) {
            return;
        }

        try {
            await issueDestinationAPI.delete(destination._id);
            // Remove from list instead of navigating since we're already on the list page
            setDestinations(destinations.filter(d => d._id !== destination._id));
        } catch (error) {
            console.error('Error deleting destination:', error);
            alert(`Failed to delete destination: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Issue Destinations</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search destinations..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="outline-secondary"
                        onClick={() => setShowDeactivated(!showDeactivated)}
                        className="me-2"
                    >
                        {showDeactivated ? 'Hide Deactivated' : 'Show Deactivated'}
                    </Button>
                    {hasPermission('destinations.modify') && (
                        <Button
                            onClick={() => navigate('/issue-destinations/create')}
                            variant="success"
                        >
                            Create Destination
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <ResizableTable
                    tableId="issue_destinations_table"
                    columns={[
                        {
                            id: 'name',
                            header: 'Name',
                            cell: (row) => (
                                <>
                                    {highlightText(row.name, searchTerm)}
                                    {!row.is_active && (
                                        <span className="ms-2 text-muted fst-italic">
                                            (Inactive)
                                        </span>
                                    )}
                                </>
                            )
                        },
                        {
                            id: 'full_name',
                            header: 'Full Name',
                            cell: (row) => highlightText(row.full_name || '', searchTerm)
                        },
                        {
                            id: 'contact',
                            header: 'Contact',
                            cell: (row) => highlightText(row.contact || '', searchTerm)
                        },
                        {
                            id: 'bill_to_address',
                            header: 'Bill To Address',
                            cell: (row) => highlightText(row.bill_to_address || '', searchTerm)
                        },
                        {
                            id: 'ship_to_address',
                            header: 'Ship To Address',
                            cell: (row) => highlightText(row.ship_to_address || '', searchTerm)
                        },
                        {
                            id: 'phone',
                            header: 'Phone',
                            cell: (row) => highlightText(row.phone || '', searchTerm)
                        },
                        {
                            id: 'email',
                            header: 'Email',
                            cell: (row) => highlightText(row.email || '', searchTerm)
                        },
                        {
                            id: 'subcategories_count',
                            header: 'Subcategories',
                            cell: (row) => (
                                <span className="badge bg-secondary">
                                    {row.subcategories_count || 0}
                                </span>
                            )
                        },
                        {
                            id: 'actions',
                            header: 'Actions',
                            cell: (row) => (
                                hasPermission('destinations.modify') && !row.is_system && (
                                    <ButtonGroup size="sm" className="gap-1">
                                        <Button
                                            variant={row.is_active ? "danger" : "success"}
                                            onClick={(e) => handleToggleActive(e, row)}
                                        >
                                            {row.is_active ? 'Deactivate' : 'Activate'}
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            onClick={(e) => handleDelete(e, row)}
                                        >
                                            Delete
                                        </Button>
                                    </ButtonGroup>
                                )
                            )
                        }
                    ]}
                    data={destinations}
                    initialColumnWidths={columnWidths}
                    onRowClick={(row) => navigate(`/issue-destinations/${row._id}`)}
                    striped
                    hover
                />
            )}
        </Container>
    )
}

export default IssueDestinationsListScreen